import { useQuery } from '@apollo/client/react';
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import { Inbox } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

import { ACTIVITY_BANNER_QUERY } from './activityBannerQueries';

export const ActivityBanner = ({ userId }: { userId: string }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { t } = useLocale();

  const { data } = useQuery(ACTIVITY_BANNER_QUERY, {
    variables: { userId },
    skip: !userId,
  });

  const leads = data?.lead_agents_aggregate?.aggregate?.count ?? 0;
  const enquiries = data?.enquiries_aggregate?.aggregate?.count ?? 0;
  const total = leads + enquiries;

  if (total < 1) {
    return null;
  }

  const parts: string[] = [];
  if (leads > 0) {
    parts.push(t('{{count}} seller leads', { count: leads }));
  }
  if (enquiries > 0) {
    parts.push(t('{{count}} direct inquiries', { count: enquiries }));
  }

  return (
    <Box sx={{ position: 'relative', maxWidth: 480 }}>
      <Box
        sx={{
          position: 'absolute',
          top: -10,
          left: -10,
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: '#ef4444',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          zIndex: 1,
        }}
      >
        {total}
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
          px: 1.5,
          py: 1,
          bgcolor: alpha(primary, 0.06),
          border: `1px solid ${alpha(primary, 0.15)}`,
          borderRadius: 1.5,
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '7px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(primary, 0.1),
            flexShrink: 0,
          }}
        >
          <Inbox size={14} color={primary} />
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.primary', lineHeight: 1.4 }}
        >
          {parts.map((part, i) => (
            <span key={part}>
              {i > 0 && ` ${t('and')} `}
              <Box component="span" sx={{ fontWeight: 700, color: primary }}>
                {part}
              </Box>
            </span>
          ))}{' '}
          {t('awaiting you')}
        </Typography>
      </Stack>
    </Box>
  );
};
